import * as PIXI from "pixi.js-legacy";
import * as particles from 'pixi-particles';

import {getRandomInt} from "../../framework/src/helpers";
import SymbolAnim from "./SymbolAnim";
// import MadmaxCommanderBase from "../../framework/src/madmax/MadmaxCommanderBase";

/**
 * Location: (framework)../src/symbols/Symbol.js
 * Location: (game)src/symbols/Symbol.js
 *
 * Template class for symbol animations. Every class Symbol{Blue|Orange|Red|Crown||Purple|Green...} extends this class.
 * Most of the symbol animations in Multi Diamonds are the same so the basic animations are defined here. The different
 * parts are defined inside the symbol files.
 *
 * @property madmax {Madmax}
 * @property commander {Commander}
 * @property layer {PIXI.Container} animations will be added to this layer
 * @property emitters {array} array of emitters
 * @property speed {number} multiplier that changes the default speed animation
 * @property _configBig {object} object with particle emitter settings
 * @property _configSmall {object} object with particle settings
 * @property texture {PIXI.Tecture} particle texture
 * @class
 */
class Symbol {
    constructor(commander,  speed) {
        this.commander = commander;
        this.layer = this.commander.layers.anims;
        this.emitters = [];

        this.speed = speed; // animation speed multiplier
        this._configBig = winEmitterConfigBig;
        this._configSmall = winEmitterConfigSmall;
        this.texture = null;
    }

    /**
     * Override to set the texture here.
     * @override
     */
    init() {
    }

    /**
     * Run the animation. Animate start first, then loop and then end.
     * Set the alpha of the symbol to 0.
     * The start loop creates SHINE sprite that is distributed the other animation functions via promises.
     * @param symbol {PIXI.Sprite} symbol
     * @param loop {number} number of loops to be played
     * @return {Promise<any>}
     */
    animate(symbol, loop) {
        this.hideSymbol(symbol);
        return new Promise (res => {
            this.start(symbol).then((shine) => { // duration - 1050ms
                this.loop(loop, shine[0]).then((shine) => { // duration - 500ms * i
                    this.end(shine, symbol).then(() => res()); // duration - 250ms
                });
            })
        }).then(() => {
            this.clearEmitters();
        });
    }

    /**
     * The beginning of the animation. Create the shine with the same position and size like symbol. Then change the
     * size to something like a "little bounce". Create reflection, ellipse and particle effects.
     * Return the promise after all these parts have ended.
     * Override for different animations.
     * @override
     */
    start(symbol) {
        return new Promise (res => {
            let promises = [];
            promises.push(this.startShine(symbol));
            promises.push(this.startReflection(symbol));
            promises.push(this.startParticles(symbol));
            promises.push(this.startEllipse(symbol));
            Promise.all(promises).then((shine) => {
                res(shine);
            });
        });
    }

    /**
     * Loop animation, short animation of the shine changing its size.
     * Override for different animation.
     * @override
     */
    loop(i,shine) {
        return new Promise(res => {
            if (i === 0) {
                res(shine);
            } else {
                createjs.Tween.get(shine.scale,{loop: true})
                    .to({x: 1, y: 1}, 500 * this.speed, createjs.Ease.linear)
                    .to({x: 1.025, y: 1.025}, 500 * this.speed, createjs.Ease.quadOut)
                    .call(() => {
                        if (--i < 1) {
                            SymbolAnim.removeTween(shine.scale);
                            res(shine);
                        }
                    });

            }
        })
    }

    /**
     * Call {@link Symbol#endShine}. Return Promise after animation ends.
     * Override for different animation.
     * @override
     */
    end(shine, symbol) {
        return new Promise (res => {
            this.endShine(shine, symbol, res);
        });
    }

    /**
     * Create the shine sprite. Make it bigger, then smaller then bigger. Return the shine as promise value when resolving.
     * Override for different animation.
     * @override
     */
    startShine(symbol) {
        return new Promise(res => {
            let shine = SymbolAnim.createSprite(symbol.x, symbol.y, symbol.name+"_shine.png");
            this.layer.addChild(shine);

            createjs.Tween.get(shine.scale)
                .to({x: 0.9, y: 0.9}, 200 * this.speed, createjs.Ease.linear)
                .to({x: 1.025, y: 1.025}, 200 * this.speed, createjs.Ease.linear)
                .wait(300 * this.speed)
                .call(() => {
                    SymbolAnim.removeTween(shine.scale);
                    res(shine);
                });
        });
    }

    /**
     * Change the size to the previous size of the shine and slowly set its opacity to 0. Set the opacity of the symbol to 1.
     * Override for different animation.
     * @override
     */
    endShine (shine, symbol, callback = () => {}) {
        createjs.Tween.get(shine)
            .wait(300*this.speed)
            .call(() => {
                this.showSymbol(symbol);
            })
            .to({alpha: 0}, 200 * this.speed, createjs.Ease.quadOut)
            .call(() => {
                SymbolAnim.removeTween(shine);
            });
        createjs.Tween.get(shine.scale)
            .to({x: 0.95, y: 0.95}, 300 * this.speed, createjs.Ease.quadOut)
            .to({x: 1, y: 1}, 200 * this.speed, createjs.Ease.quadOut)
            .call(() => {
                SymbolAnim.removeTween(shine.scale);
                callback();
            });
    }

    /**
     * Create Ellipse. Scale it and change its opacity slowly.
     * Override for different animation.
     * @override
     */
    startEllipse(symbol) {
        return new Promise(res => {
            let ellipse = SymbolAnim.createSprite(symbol.x, symbol.y, "elipse.png");
            ellipse.scale.set(0, 0);
            this.layer.addChild(ellipse);

            createjs.Tween.get(ellipse.scale)
                .wait(200*this.speed)
                .to({x: .9, y: .9}, 600*this.speed, createjs.Ease.circIn())
                .call(() => {
                    SymbolAnim.removeTween(ellipse.scale);
                    res();
                });

            createjs.Tween.get(ellipse)
                .wait(500*this.speed)
                .to({alpha: 0}, 300*this.speed, createjs.Ease.circIn())
                .call(() => {
                    SymbolAnim.removeTween(ellipse);
                });
        });
    }

    /**
     * Create overlay sprite, container, mask and set the mask as container mask. Animate overlay position to
     * make the reflection effect.
     * Override for different animation.
     * @override
     */
    startReflection(symbol) {
        return new Promise(res => {
            let container = new PIXI.Container();
            let overlay = SymbolAnim.createSprite(symbol.x-100, symbol.y, "overlay.png");
            overlay.alpha = 0;

            let mask = SymbolAnim.createMask(symbol.x, symbol.y, symbol.name);
            container.mask = mask;
            container.addChild(mask);
            container.addChild(overlay);
            this.layer.addChild(container);

            createjs.Tween.get(overlay)
                .wait(1000*this.speed)
                .to({alpha: 1}, 50*this.speed, createjs.Ease.linear)
                .to({x: symbol.x+150, y: symbol.y}, 500*this.speed, createjs.Ease.linear)
                .to({alpha: 0}, 50*this.speed, createjs.Ease.quadOut)
                .call(() => {
                    SymbolAnim.removeTween(overlay);
                    SymbolAnim.removeSprite(overlay, container);
                    SymbolAnim.removeSprite(mask, container);
                    SymbolAnim.removeSprite(container, this.layer);
                    res();
                });
        });
    }

    /**
     * Create emitters, set their properties and positions and run them. Every symbol has different position and type of
     * emitters so this is usually implemented inside every symbol class.
     * Override for different animation.
     * @override
     */
    startParticles(symbol) {
    }

    /**
     * Create emitter and return it. Add it to this.layer with Big settings and texture.
     * @return PIXI.particles.Emitter
     */
    createBigEmitter(x, y) {
        let emitter = new particles.Emitter(this.layer, this.texture , this._configBig);
        emitter.updateSpawnPos(x, y);
        this.emitters.push(emitter);
        return emitter;
    }

    /**
     * Create emitter and return it. Add it to this.layer with Small settings and texture.
     * @return PIXI.particles.Emitter
     */
    createSmallEmitter(x, y) {
        let emitter = new particles.Emitter(this.layer, this.texture , this._configSmall);
        emitter.updateSpawnPos(x, y);
        this.emitters.push(emitter);
        return emitter;
    }

    /**
     * Creates a spark that is displayed when the small emitters explode.
     * The rotation is random. Only changes its size.
     * Override for different animation.
     * @override
     */
    createSpark(x, y, scale = 0.7) {
        let spark = SymbolAnim.createSprite(x, y, "spark.png");
        spark.alpha = 0;
        spark.rotation = getRandomInt(0,360);
        spark.scale.set(scale,scale);

        createjs.Tween.get(spark.scale)
            .to({x: scale+0.4, y: scale+0.4}, 500*this.speed, createjs.Ease.linear)
            .to({x: 0, y: 0}, 500*this.speed, createjs.Ease.linear)
            .call(() => {
                SymbolAnim.removeTween(spark.scale);
            });

        createjs.Tween.get(spark)
            .to({alpha: 1}, 200*this.speed, createjs.Ease.linear)
            .to({alpha: 0}, 800*this.speed, createjs.Ease.linear)
            .call(() => {
                SymbolAnim.removeTween(spark);
            });
        this.layer.addChild(spark);
    }

    hideSymbol(symbol) {
        this.commander.winSymbolSprites[symbol.reelX][symbol.reelY].visible = false;
    }

    showSymbol(symbol) {
        this.commander.winSymbolSprites[symbol.reelX][symbol.reelY].visible = true;
    }

    clearEmitters() {
        for (let emitter of this.emitters) {
            emitter.cleanup();
            emitter.destroy();
        }
        this.emitters = [];
    }
}
let winEmitterConfigBig = {
    "alpha": {
        "start": 1,
        "end": 1
    },
    "scale": {
        "start": 0.8,
        "end": 0.5,
        "minimumScaleMultiplier": 1
    },
    "color": {
        "start": "#ffffff",
        "end": "#ffffff"
    },
    "speed": {
        "start": 300,
        "end": 100,
        "minimumSpeedMultiplier": 1
    },
    "acceleration": {
        "x": 0,
        "y": 0
    },
    "maxSpeed": 0,
    "startRotation": {
        "min": 0,
        "max": 0
    },
    "noRotation": false,
    "rotationSpeed": {
        "min": 0,
        "max": 0
    },
    "lifetime": {
        "min": 0.3,
        "max": 0.5
    },
    "blendMode": "normal",
    "frequency": 0.007,
    "emitterLifetime": 0.17,
    "maxParticles": 50,
    "pos": {
        "x": 0,
        "y": 0
    },
    "addAtBack": false,
    "spawnType": "ring",
    "spawnCircle": {
        "x": 0,
        "y": 0,
        "r": 20,
        "minR": 20
    }
};
let winEmitterConfigSmall = {
    "alpha": {
        "start": 1,
        "end": 1
    },
    "scale": {
        "start": 0.8,
        "end": 0.5,
        "minimumScaleMultiplier": 1
    },
    "color": {
        "start": "#ffffff",
        "end": "#ffffff"
    },
    "speed": {
        "start": 200,
        "end": 50,
        "minimumSpeedMultiplier": 1
    },
    "acceleration": {
        "x": 0,
        "y": 0
    },
    "maxSpeed": 0,
    "startRotation": {
        "min": 0,
        "max": 0
    },
    "noRotation": false,
    "rotationSpeed": {
        "min": 0,
        "max": 0
    },
    "lifetime": {
        "min": 0.3,
        "max": 0.5
    },
    "blendMode": "normal",
    "frequency": 0.006,
    "emitterLifetime": 0.1,
    "maxParticles": 50,
    "pos": {
        "x": 0,
        "y": 0
    },
    "addAtBack": false,
    "spawnType": "ring",
    "spawnCircle": {
        "x": 0,
        "y": 0,
        "r": 0,
        "minR": 0
    }
};
export default Symbol;